import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { HexisSymbol } from '@marcianus/design/components';
import { Section, SectionHeading, Stack } from '@marcianus/design/components';
import { stripMd } from '@/lib/text';
import { markRead, markAllRead } from '../services/alertas.api';
import { getHexisInbox, deepLink, type InboxItem, type HexisInbox } from '../services/inbox.api';
import { useTranslation } from 'react-i18next';

// ── Caixa inteligente — peças partilhadas ────────────────────────────────────
// As DUAS superfícies de notificações (a página /notificacoes e o separador
// "Alertas" do overlay Hexis) consomem daqui. Mesma query (mesma cache, uma só
// chamada), mesmos cartões → é impossível uma ficar burra enquanto a outra pensa.

// Nunca mostramos o enum cru ao utilizador (diretiva de design).
const KINDS = ['match', 'event', 'xp', 'connection', 'content', 'alert', 'hexis', 'mission', 'system'] as const;

// A cor diz a urgência que o Hexis atribuiu — sem inventar níveis novos.
const PRIORITY_COLOR: Record<string, string> = {
  high: 'var(--gold)', normal: 'var(--purple)', low: 'var(--border-2)',
};

export const INBOX_KEY = ['hexis', 'inbox'] as const;

/** A caixa, decidida pelo Hexis + as ações que operam sobre ela. */
export function useInbox() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const inbox = useQuery({ queryKey: INBOX_KEY, queryFn: getHexisInbox, staleTime: 30_000 });

  // Ler afeta a caixa E o badge do sino — invalidamos ambos.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: INBOX_KEY });
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const readOne = useMutation({ mutationFn: (id: string) => markRead(id), onSuccess: refresh });
  const readAll = useMutation({ mutationFn: markAllRead, onSuccess: refresh });

  // Abrir = ler + ir ao sítio de que a notificação fala (CTA real, via ROUTES).
  function open(n: InboxItem) {
    if (!n.read) readOne.mutate(n.id);
    const to = deepLink(n);
    if (to) navigate(to);
  }

  return { inbox, open, readAll, refresh };
}

/** O briefing do córtex — voz do Hexis, texto plano (diretiva: sem markdown).
 *  É o `.hint-card` do sistema: uma frase que enquadra, com fundo roxo. Estava
 *  escrito à mão com a sua própria borda (--purple-20 em vez de
 *  --border-accent) e o seu próprio corpo (12.5/13.5px, meios-pixéis). */
export function Briefing({ briefing, compact = false }: {
  briefing: NonNullable<HexisInbox['briefing']>; compact?: boolean;
}) {
  const { t } = useTranslation('hexis');
  return (
    <div className="hint-card" style={{ alignItems: 'flex-start' }}>
      <span style={{ flexShrink: 0, marginTop: 1 }}><HexisSymbol size={compact ? 18 : 22} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: 'var(--fg-100)', lineHeight: 1.5 }}>{stripMd(briefing.text)}</span>
        {/* `.frase` porque isto NÃO é um rótulo: o foco do briefing é escrito
            pelo Hexis e pode ser uma frase inteira. Num chip normal
            (`nowrap`, 24px fixos) ela esticava para fora do card. */}
        {briefing.focus && (
          <span className="chip purple frase" style={{ marginTop: 7 }}>
            {t('inbox.first', { focus: stripMd(briefing.focus) })}
          </span>
        )}
      </span>
    </div>
  );
}

/** A contagem por ler + «marcar tudo lido». As DUAS superfícies mostravam-na de
 *  forma diferente: a página com um `.chip` e um `btn-ghost`, o overlay com um
 *  `.eyebrow` e um `.section-link`. Mesma informação, mesma ação — uma peça. */
export function InboxStatus({ unread, onReadAll, pending }: {
  unread: number; onReadAll: () => void; pending?: boolean;
}) {
  const { t } = useTranslation('hexis');
  return (
    <div className="sect-head">
      <span className="chip">{unread ? t('inbox.unread', { count: unread }) : t('inbox.allRead')}</span>
      {unread > 0 && (
        <button className="btn btn-ghost btn-sm" onClick={onReadAll} disabled={pending}>
          {pending ? t('inbox.marking') : t('inbox.markAll')}
        </button>
      )}
    </div>
  );
}

/** Uma faixa da triagem (Agir agora / Para ti / Histórico). */
export function Lane({ title, hint, items, onOpen, dim = false }: {
  title: string; hint?: string; items: InboxItem[]; onOpen: (n: InboxItem) => void; dim?: boolean;
}) {
  if (!items.length) return null;
  // `SectionHeading` como em toda a app. Era `.section-title` — 18px sans — e
  // esta era a última tela onde as secções ainda se nomeavam assim: no mesmo
  // ecrã conviviam títulos grandes e os rótulos mono de todos os outros.
  return (
    <Section>
      <SectionHeading label={title} action={<span className="chip">{items.length}</span>} />
      {hint && <p className="sect-note">{hint}</p>}
      <Stack tight style={{ opacity: dim ? 0.55 : 1 }}>
        {items.map((n) => <NotifCard key={n.id} n={n} onOpen={onOpen} />)}
      </Stack>
    </Section>
  );
}

export function NotifCard({ n, onOpen }: { n: InboxItem; onOpen: (n: InboxItem) => void }) {
  const { t } = useTranslation('hexis');
  const unread = !n.read;
  const accent = PRIORITY_COLOR[n.priority || 'normal'] || 'var(--purple)';
  const headline = stripMd(n.title || n.body || n.text || t('inbox.notification'));
  // Só mostramos o corpo se acrescentar algo ao título (evita a mesma frase 2x).
  const body = stripMd(n.body || n.text || '');
  const showBody = !!body && body !== headline;
  const to = deepLink(n);

  return (
    <button
      className="card"
      onClick={() => onOpen(n)}
      style={{
        display: 'flex', gap: 0, padding: 0, textAlign: 'left', width: '100%',
        overflow: 'hidden', opacity: unread ? 1 : 0.72,
      }}
    >
      {/* Barra de acento = urgência decidida pelo Hexis. */}
      <span style={{ width: 3, alignSelf: 'stretch', background: unread ? accent : 'transparent', flexShrink: 0 }} />

      <span style={{ flex: 1, minWidth: 0, padding: '11px 12px' }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{
            flex: 1, minWidth: 0, fontSize: 'var(--fs-md)', lineHeight: 1.4,
            fontWeight: unread ? 600 : 500, color: 'var(--fg-100)',
          }}>
            {headline}
          </span>
          {n.time && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--fg-40)', flexShrink: 0 }}>{n.time}</span>}
        </span>

        {showBody && (
          <span style={{ display: 'block', fontSize: 'var(--fs-body)', lineHeight: 1.45, color: 'var(--fg-60)', marginTop: 3 }}>
            {body}
          </span>
        )}

        {/* PORQUÊ AGORA — a explicação do Hexis. É isto que separa uma caixa
            inteligente de uma lista: o utilizador percebe porque está a ver isto. */}
        {n.why && (
          <span style={{ display: 'flex', gap: 5, alignItems: 'flex-start', marginTop: 6 }}>
            <span style={{ flexShrink: 0, marginTop: 1, opacity: 0.9 }}><HexisSymbol size={11} /></span>
            <span style={{ fontSize: 'var(--fs-label)', lineHeight: 1.4, color: 'var(--purple-50)' }}>{stripMd(n.why)}</span>
          </span>
        )}

        {/* Meta ÚNICA (diretiva de design: não empilhar etiquetas). */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7 }}>
          <span className="chip">
            {(KINDS as readonly string[]).includes(n.type || 'system') ? t(`inbox.kind.${(n.type || 'system') as (typeof KINDS)[number]}`) : t('inbox.kind.alert')}
          </span>
          {to && (
            <span style={{ fontSize: 'var(--fs-label)', fontWeight: 600, color: 'var(--fg-80)', marginLeft: 'auto' }}>
              {n.cta_label || t('inbox.open')} →
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
